import {
  Component,
  OnInit
} from '@angular/core';

import {
  ActivatedRoute,
  Router
} from '@angular/router';
import { ExtractedIdDetails } from 'src/app/core/ExtractedIdDetails';

import {
  AdminService
} from 'src/app/services/admin/admin.service';
import { FileUploadService } from 'src/app/services/file-upload/file-upload.service';




interface VocCompanyForm {

  companyName: string;

  abn: string;

  contactPerson: string;

  position: string;

  companyEmail: string;

  phone: string;
}


interface VocParticipantRegistration {

  participantNo: number;

  fullName: string;


  // ============================================================
  // PARTICIPANT DETAILS
  // ============================================================

  firstName: string;

  lastName: string;

  dateOfBirth: string;

  gender: string;

  emailAddress: string;

  mobileNumber: string;


  // ============================================================
  // QUALIFICATION
  // ============================================================

  relevantQualification: string;

  qualificationCertificateNumber: string;

  issuingOrganisation: string;

  dateIssuedExpiry: string;

  certificateAvailable:
  boolean | null;


  // ============================================================
  // CERTIFICATE UPLOAD
  // ============================================================

  certificateFileName: string;

  certificateFileUrl: string;

  isCertificateUploading: boolean;

  certificateUploadError: string;

  certificateFileType: string;
  // ============================================================
  // IDENTITY
  // ============================================================

  idType: string;

  idNumber: string;

  idExpiryDate: string;


  // ============================================================
  // ID DOCUMENT UPLOAD
  // ============================================================

  idFileName: string;

  idFileUrl: string;

  isIdUploading: boolean;

  idUploadError: string;

  idFileType: string;

  isIdReading: boolean;

  idOcrProgress: number;

  nationality: string;
  // ============================================================
  // DECLARATION
  // ============================================================

  declarationTrueCorrect: boolean;

  declarationAdditionalEvidence: boolean;

  declarationPracticalCompetency: boolean;

  applicantName: string;

  signature: string;

  declarationDate: string;


  // ============================================================
  // EXISTING TRAINING SELECTION
  // ============================================================

  uocCourses: any[];

  vocCourses: any[];

  coaCourses: any[];
}

@Component({
  selector: 'app-voc-company-form',
  templateUrl: './voc-company-form.component.html',
  styleUrl: './voc-company-form.component.scss'
})
export class VocCompanyFormComponent implements OnInit {

  // Store enquiry ID
  enquiryId = 0;

  // Store enquiry details
  enquiry: any = null;

  // Store enquiry body
  enquiryBody: any = null;

  // Store client type
  clientType: 'CORPORATE' | 'GENERAL' = 'CORPORATE';


  // Track loading state
  isLoading = false;

  // Track submission state
  isSubmitting = false;

  // Store error message
  errorMessage = '';

  // Store success message
  successMessage = '';


  // Store company form details
  companyForm:
    VocCompanyForm = {

      companyName: '',

      abn: '',

      contactPerson: '',

      position: '',

      companyEmail: '',

      phone: ''
    };


  // Track submitted state
  alreadySubmitted: boolean = false;

  // Store participant registrations
  participants:
    VocParticipantRegistration[] = [];


  constructor(
    private route:
      ActivatedRoute,

    private adminService:
      AdminService,

    private uploadService:
      FileUploadService,

    private router:
      Router
  ) {
  }


  // Initialise enquiry details
  ngOnInit(): void {

    // Get enquiry ID from URL
    const enquiryId =
      Number(
        this.route
          .snapshot
          .queryParamMap
          .get(
            'enquiryId'
          )
      );


    // Validate enquiry ID
    if (!enquiryId) {

      this.errorMessage =
        'Invalid enquiry link.';

      return;
    }


    // Store enquiry ID
    this.enquiryId =
      enquiryId;


    // Load enquiry details
    this.loadEnquiry();
  }




  // ============================================================
  // LOAD APPROVED ENQUIRY
  // ============================================================

  loadEnquiry(): void {

    this.isLoading = true;
    this.errorMessage = '';
    this.alreadySubmitted = false;

    this.adminService
      .getApprovedXEnquiry(this.enquiryId)
      .subscribe({

        next: (response: any) => {

          this.isLoading = false;

          // ====================================================
          // NO RECORD = FORM ALREADY SUBMITTED
          // ====================================================

          if (!response) {

            this.alreadySubmitted = true;
            return;

          }

          this.enquiry = response;

          try {

            this.enquiryBody =
              JSON.parse(
                response?.body || '{}'
              );

            this.clientType =
              (
                response?.clientType ||
                this.enquiryBody?.clientType ||
                'CORPORATE'
              )
                .toString()
                .trim()
                .toUpperCase() as
              'CORPORATE' | 'GENERAL';

          } catch (error) {

            console.error(
              'Unable to parse enquiry body:',
              error
            );

            this.errorMessage =
              'Unable to read enquiry details.';

            return;
          }

          this.populateExistingData();
        },

        error: (error) => {

          console.error(
            'Unable to load enquiry:',
            error
          );

          this.isLoading = false;

          // ====================================================
          // 404 = RECORD NOT AVAILABLE / ALREADY SUBMITTED
          // ====================================================

          if (error?.status === 404) {

            this.alreadySubmitted = true;
            return;

          }

          // ====================================================
          // ACTUAL ERROR
          // ====================================================

          this.errorMessage =
            typeof error?.error === 'string'
              ? error.error
              : 'Unable to load enquiry details.';
        }

      });
  }


  // ============================================================
  // POPULATE EXISTING SUBMITTED DATA
  // ============================================================

  populateExistingData(): void {

    if (
      this.clientType ===
      'GENERAL'
    ) {

      this.populateGeneralEnquiry();

      return;
    }


    this.populateCorporateEnquiry();
  }

  // ============================================================
  // POPULATE CORPORATE DATA
  // ============================================================

  populateCorporateEnquiry(): void {

    const client =
      this.enquiryBody
        ?.clientDetails
      || {};


    this.companyForm = {

      companyName:
        client.companyName
        || '',

      abn:
        '',

      contactPerson:
        client.requestedBy
        || '',

      position:
        '',

      companyEmail:
        client.companyEmail
        || '',

      phone:
        client.requestedByPhone
        || ''
    };


    const enquiryParticipants =
      this.enquiryBody
        ?.participants
      || [];


    this.participants =
      enquiryParticipants.map(
        (
          participant: any,
          index: number
        ) => {

          const name =
            this.splitFullName(
              participant?.fullName
              || ''
            );


          return this.createParticipant({

            participantNo:
              participant
                ?.participantNo
              || index + 1,

            fullName:
              participant
                ?.fullName
              || '',

            firstName:
              name.firstName,

            lastName:
              name.lastName,

            applicantName:
              participant
                ?.fullName
              || '',

            uocCourses:
              participant
                ?.uocCourses
              || [],

            vocCourses:
              participant
                ?.vocCourses
              || [],

            coaCourses: participant
              ?.coaCourses
              || []
          });
        }
      );
  }

  // ============================================================
  // POPULATE GENERAL ENQUIRY
  // ============================================================
  populateGeneralEnquiry(): void {

    const generalParticipant =
      this.enquiryBody
        ?.participant
      || {};


    const givenName =
      generalParticipant
        ?.givenName
      || '';

    const familyName =
      generalParticipant
        ?.familyName
      || '';

    const fullName =
      `${givenName} ${familyName}`
        .trim();


    this.companyForm = {

      companyName:
        '',

      abn:
        '',

      contactPerson:
        fullName,

      position:
        '',

      companyEmail:
        generalParticipant
          ?.email
        || this.enquiry
          ?.sentBy
        || '',

      phone:
        ''
    };


    const uocCourses: any[] =
      [];


    const primaryCourse =
      this.enquiryBody
        ?.primaryCourse;


    if (primaryCourse) {

      uocCourses.push({

        id:
          primaryCourse
            ?.courseId,

        courseId:
          primaryCourse
            ?.courseId,

        courseCode:
          primaryCourse
            ?.courseCode
          || '',

        courseTitle:
          primaryCourse
            ?.courseTitle
          || '',

        description:
          primaryCourse
            ?.description
          || '',

        occurrence:
          primaryCourse
            ?.occurrence
          || null
      });
    }


    const additionalUOCCourses =
      this.enquiryBody
        ?.additionalUOCCourses
      || [];


    uocCourses.push(
      ...additionalUOCCourses
    );


    const vocCourses =
      this.enquiryBody
        ?.vocCourses
      || [];


    this.participants = [

      this.createParticipant({

        participantNo:
          1,

        fullName:
          fullName,

        firstName:
          givenName,

        lastName:
          familyName,

        emailAddress:
          generalParticipant
            ?.email
          || '',

        applicantName:
          fullName,

        uocCourses:
          uocCourses,

        vocCourses:
          vocCourses
      })
    ];
  }

  // ============================================================
  // CREATE PARTICIPANT
  // ============================================================
  createParticipant(
    values: Partial<VocParticipantRegistration> = {}
  ): VocParticipantRegistration {

    return {

      participantNo:
        values.participantNo
        || 1,

      fullName:
        values.fullName
        || '',

      firstName:
        values.firstName
        || '',

      lastName:
        values.lastName
        || '',

      dateOfBirth:
        values.dateOfBirth
        || '',

      gender:
        values.gender
        || '',

      emailAddress:
        values.emailAddress
        || '',

      mobileNumber:
        values.mobileNumber
        || '',

      relevantQualification:
        values.relevantQualification
        || '',

      qualificationCertificateNumber:
        values.qualificationCertificateNumber
        || '',

      issuingOrganisation:
        values.issuingOrganisation
        || '',

      dateIssuedExpiry:
        values.dateIssuedExpiry
        || '',

      certificateAvailable:
        values.certificateAvailable
        ?? null,

      certificateFileName:
        values.certificateFileName
        || '',

      certificateFileUrl:
        values.certificateFileUrl
        || '',

      isCertificateUploading:
        values.isCertificateUploading
        ?? false,

      certificateUploadError:
        values.certificateUploadError
        || '',

      certificateFileType:
        values.certificateFileType
        || '',

      idType:
        values.idType
        || '',

      idNumber:
        values.idNumber
        || '',

      idExpiryDate:
        values.idExpiryDate
        || '',

      idFileName:
        values.idFileName
        || '',

      idFileUrl:
        values.idFileUrl
        || '',

      isIdUploading:
        values.isIdUploading
        ?? false,

      idUploadError:
        values.idUploadError
        || '',

      idFileType:
        values.idFileType
        || '',

      isIdReading:
        values.isIdReading
        ?? false,

      idOcrProgress:
        values.idOcrProgress
        ?? 0,

      nationality:
        values.nationality
        || '',

      declarationTrueCorrect:
        values.declarationTrueCorrect
        ?? false,

      declarationAdditionalEvidence:
        values.declarationAdditionalEvidence
        ?? false,

      declarationPracticalCompetency:
        values.declarationPracticalCompetency
        ?? false,

      applicantName:
        values.applicantName
        || '',

      signature:
        values.signature
        || '',

      declarationDate:
        values.declarationDate
        || new Date().toLocaleDateString('en-CA'),

      uocCourses:
        values.uocCourses
        || [],

      vocCourses:
        values.vocCourses
        || [],

      coaCourses:
        values.coaCourses
        || [],
    };
  }


  // ============================================================
  // SPLIT FULL NAME
  // ============================================================

  splitFullName(
    fullName: string
  ): {
    firstName: string;
    lastName: string;
  } {

    const value =
      fullName
        ?.trim()
      || '';


    if (!value) {

      return {

        firstName: '',

        lastName: ''
      };
    }


    const parts =
      value.split(
        /\s+/
      );


    return {

      firstName:
        parts[0],

      lastName:
        parts
          .slice(1)
          .join(' ')
    };
  }


  // ============================================================
  // CERTIFICATE AVAILABLE CHANGE
  // ============================================================

  onCertificateAvailableChange(
    participantIndex: number
  ): void {

    const participant =
      this.participants[
      participantIndex
      ];


    if (
      participant
        .certificateAvailable !==
      true
    ) {

      participant
        .certificateFileName =
        '';

      participant
        .certificateFileUrl =
        '';

      participant
        .certificateUploadError =
        '';
    }
  }


  // ============================================================
  // CERTIFICATE FILE SELECTED
  // ============================================================

  onCertificateFileSelected(
    event: Event,
    participantIndex: number
  ): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    const participant =
      this.participants[
      participantIndex
      ];


    participant.certificateUploadError =
      '';


    if (!file) {

      participant.certificateFileName =
        '';

      participant.certificateFileUrl =
        '';

      return;
    }


    // ==========================================================
    // ALLOWED FILE TYPES
    // ==========================================================

    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];


    const allowedExtensions = [
      '.pdf',
      '.jpg',
      '.jpeg',
      '.png',
      '.webp'
    ];


    const fileName =
      file.name.toLowerCase();


    const hasValidExtension =
      allowedExtensions.some(
        extension =>
          fileName.endsWith(
            extension
          )
      );


    const hasValidMimeType =
      allowedMimeTypes.includes(
        file.type
      );


    if (
      !hasValidMimeType &&
      !hasValidExtension
    ) {

      participant.certificateUploadError =
        'Please upload a PDF, JPG, JPEG, PNG or WebP file.';


      participant.certificateFileName =
        '';

      participant.certificateFileUrl =
        '';


      input.value =
        '';

      return;
    }


    // ==========================================================
    // MAXIMUM FILE SIZE - 10MB
    // ==========================================================

    const maximumFileSize =
      10 * 1024 * 1024;


    if (
      file.size >
      maximumFileSize
    ) {

      participant.certificateUploadError =
        'The file must be smaller than 10 MB.';


      participant.certificateFileName =
        '';

      participant.certificateFileUrl =
        '';


      input.value =
        '';

      return;
    }


    // ==========================================================
    // START UPLOAD
    // ==========================================================

    participant.certificateFileName =
      file.name;

    participant.certificateFileUrl =
      '';

    participant.isCertificateUploading =
      true;


    this.uploadService
      .uploadSingleFile(
        file,
        'voc-form'
      )
      .subscribe({

        next: (response: any) => {

          participant.isCertificateUploading =
            false;


          const uploadedUrl =
            response?.publicUrl || '';


          if (!uploadedUrl) {

            participant.certificateUploadError =
              'The file was uploaded, but the file URL was not returned.';

            return;
          }


          participant.certificateFileUrl =
            uploadedUrl;
        },


        error: (error) => {

          console.error(
            'Certificate upload failed:',
            error
          );


          participant.isCertificateUploading =
            false;


          participant.certificateUploadError =
            'File upload failed. Please try again.';


          participant.certificateFileName =
            '';

          participant.certificateFileUrl =
            '';


          input.value =
            '';
        }
      });
  }

  // ============================================================
  // ON ID FILE SELECTED
  // ============================================================
  onIdFileSelected(
    event: Event,
    participantIndex: number
  ): void {

    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    const participant =
      this.participants[
      participantIndex
      ];


    participant.idUploadError =
      '';


    // ==========================================================
    // ID TYPE MUST BE SELECTED
    // ==========================================================

    if (
      !participant.idType
    ) {

      participant.idUploadError =
        'Please select an ID type first.';

      input.value =
        '';

      return;
    }


    // ==========================================================
    // NO FILE
    // ==========================================================

    if (!file) {

      participant.idFileName =
        '';

      participant.idFileUrl =
        '';

      return;
    }


    // ==========================================================
    // ALLOWED FILE TYPES
    // ==========================================================

    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];


    const allowedExtensions = [
      '.pdf',
      '.jpg',
      '.jpeg',
      '.png',
      '.webp'
    ];


    const fileName =
      file.name.toLowerCase();


    const hasValidExtension =
      allowedExtensions.some(
        extension =>
          fileName.endsWith(
            extension
          )
      );


    const hasValidMimeType =
      allowedMimeTypes.includes(
        file.type
      );


    if (
      !hasValidMimeType &&
      !hasValidExtension
    ) {

      participant.idUploadError =
        'Please upload a PDF, JPG, JPEG, PNG or WebP file.';

      participant.idFileName =
        '';

      participant.idFileUrl =
        '';

      input.value =
        '';

      return;
    }


    // ==========================================================
    // MAXIMUM 10 MB
    // ==========================================================

    const maximumFileSize =
      10 * 1024 * 1024;


    if (
      file.size >
      maximumFileSize
    ) {

      participant.idUploadError =
        'The file must be smaller than 10 MB.';

      participant.idFileName =
        '';

      participant.idFileUrl =
        '';

      input.value =
        '';

      return;
    }


    // ==========================================================
    // SET FILE
    // ==========================================================

    participant.idFileName =
      file.name;

    participant.idFileUrl =
      '';


    // ==========================================================
    // OCR
    //
    // Tesseract works directly with image files.
    // Do not OCR PDF here.
    // ==========================================================

    const isImage =
      file.type ===
      'image/jpeg' ||
      file.type ===
      'image/png' ||
      file.type ===
      'image/webp';
 

    // ==========================================================
    // START FILE UPLOAD
    // ==========================================================

    participant.isIdUploading =
      true;


    this.uploadService
      .uploadSingleFile(
        file,
        'VOC-form'
      )
      .subscribe({

        next: (
          response: any
        ) => {

          participant.isIdUploading =
            false;


          const uploadedUrl =
            response?.publicUrl ||
            '';


          if (
            !uploadedUrl
          ) {

            participant.idUploadError =
              'The file was uploaded, but the file URL was not returned.';

            return;
          }


          participant.idFileUrl =
            uploadedUrl;

        },


        error: (
          error
        ) => {

          console.error(
            'ID document upload failed:',
            error
          );


          participant.isIdUploading =
            false;


          participant.idUploadError =
            'File upload failed. Please try again.';


          participant.idFileName =
            '';

          participant.idFileUrl =
            '';


          input.value =
            '';

        }

      });

  }


  // ============================================================
  // CHECK UPLOADS
  // ============================================================

  hasUploadingFiles(): boolean {

    return this.participants.some(
      participant =>
        participant
          .isCertificateUploading
        ||
        participant
          .isIdUploading
        ||
        participant
          .isIdReading
    );
  }


  // ============================================================
  // VALIDATE
  // ============================================================

  validateForm(): boolean {

    this.errorMessage =
      '';


    if (
      this.clientType ===
      'CORPORATE'
      &&
      !this.companyForm
        .companyName
        ?.trim()
    ) {

      this.errorMessage =
        'Company name is required.';

      return false;
    }




    /*  if (
       !this.companyForm
         .contactPerson
         ?.trim()
     ) {
 
       this.errorMessage =
         'Contact person is required.';
 
       return false;
     }
 
  */
    if (
      this.clientType ===
      'CORPORATE'
      &&
      !this.companyForm
        .companyEmail
        ?.trim()
    ) {

      this.errorMessage =
        'Company email is required.';

      return false;
    }


    /*  if (
       !this.companyForm
         .phone
         ?.trim()
     ) {
 
       this.errorMessage =
         'Company phone number is required.';
 
       return false;
     }
  */

    if (
      this.hasUploadingFiles()
    ) {

      this.errorMessage =
        'Please wait until all documents have finished uploading.';

      return false;
    }


    for (
      let index = 0;
      index < this.participants.length;
      index++
    ) {

      const participant =
        this.participants[
        index
        ];


      if (
        !participant
          .firstName
          ?.trim()
      ) {

        this.errorMessage =
          `First name is required for Participant ${index + 1}.`;

        return false;
      }


      if (
        !participant
          .lastName
          ?.trim()
      ) {

        this.errorMessage =
          `Last name is required for Participant ${index + 1}.`;

        return false;
      }

      /* 
            if (
              !participant
                .dateOfBirth
            ) {
      
              this.errorMessage =
                `Date of birth is required for ${participant.fullName}.`;
      
              return false;
            }
       */

      if (
        !participant
          .emailAddress
          ?.trim()
      ) {

        this.errorMessage =
          `Email address is required for ${participant.fullName}.`;

        return false;
      }


      /*    if (
           !participant
             .mobileNumber
             ?.trim()
         ) {
   
           this.errorMessage =
             `Mobile number is required for ${participant.fullName}.`;
   
           return false;
         } */


      // ========================================================
      // CERTIFICATE URL REQUIRED WHEN YES
      // ========================================================

      if (
        participant
          .certificateAvailable ===
        true
        &&
        !participant
          .certificateFileUrl
      ) {

        this.errorMessage =
          `Please upload the certificate for ${participant.fullName}.`;

        return false;
      }

      /* 
            if (
              !participant.idType
            ) {
      
              this.errorMessage =
                `ID type is required for ${participant.fullName}.`;
      
              return false;
            } */

      /* 
            if (
              !participant
                .idNumber
                ?.trim()
            ) {
      
              this.errorMessage =
                `ID number is required for ${participant.fullName}.`;
      
              return false;
            }
       */

      /*  if (
         !participant
           .idFileUrl
       ) {
 
         this.errorMessage =
           `Please upload the ID document for ${participant.fullName}.`;
 
         return false;
       } */


      if (
        !participant
          .declarationTrueCorrect
        ||
        !participant
          .declarationAdditionalEvidence
        ||
        !participant
          .declarationPracticalCompetency
      ) {

        this.errorMessage =
          `Please accept all declarations for ${participant.fullName}.`;

        return false;
      }

      // ========================================================
      // SIGNATURE REQUIRED
      // ========================================================

      if (
        !participant
          .signature
          ?.trim()
      ) {

        this.errorMessage =
          `Signature is required for ${participant.fullName || `Participant ${index + 1}`}.`;

        return false;
      }
    }


    return true;
  }


  // ============================================================
  // BUILD FINAL REGISTRATION DATA
  // ============================================================

  buildRegistrationData(): any {

    return {

      enquiryId:
        this.enquiryId,


      // ========================================================
      // ORIGINAL ENQUIRY DETAILS
      // ========================================================

      enquiryDetails: {

        clientType:
          this.clientType,

        clientDetails:
          this.enquiryBody
            ?.clientDetails
          || {},

        bookingConfiguration:
          this.enquiryBody
            ?.bookingConfiguration
          || {},

        participant:
          this.enquiryBody
            ?.participant
          || {},

        primaryCourse:
          this.enquiryBody
            ?.primaryCourse
          || {},

        additionalUOCCourses:
          this.enquiryBody
            ?.additionalUOCCourses
          || [],

        vocCourses:
          this.enquiryBody
            ?.vocCourses
          || [],

        others:
          this.enquiryBody
            ?.others
          || [],

        additionalComments:
          this.enquiryBody
            ?.additionalComments
          || ''
      },


      // ========================================================
      // COMPLETED COMPANY DETAILS
      // ========================================================

      companyDetails: {

        companyName:
          this.companyForm
            .companyName
            ?.trim()
          || '',

        abn:
          this.companyForm
            .abn
            ?.trim()
          || '',

        contactPerson:
          this.companyForm
            .contactPerson
            ?.trim()
          || '',

        position:
          this.companyForm
            .position
            ?.trim()
          || '',

        companyEmail:
          this.companyForm
            .companyEmail
            ?.trim()
          || '',

        phone:
          this.companyForm
            .phone
            ?.trim()
          || ''
      },


      // ========================================================
      // PARTICIPANTS
      // ========================================================

      participantRegistrations:
        this.participants.map(
          participant => {

            return {

              participantNo:
                participant
                  .participantNo,


              // =================================================
              // PARTICIPANT DETAILS
              // =================================================

              firstName:
                participant
                  .firstName
                  ?.trim()
                || '',

              lastName:
                participant
                  .lastName
                  ?.trim()
                || '',

              dateOfBirth:
                participant
                  .dateOfBirth
                || null,

              gender:
                participant
                  .gender
                || '',

              emailAddress:
                participant
                  .emailAddress
                  ?.trim()
                || '',

              mobileNumber:
                participant
                  .mobileNumber
                  ?.trim()
                || '',


              // =================================================
              // TRAINING ALREADY SELECTED
              // =================================================

              uocCourses:
                participant
                  .uocCourses,

              vocCourses:
                participant
                  .vocCourses,


              coaCourses:
                participant
                  .coaCourses
                || [],
              // =================================================
              // QUALIFICATION
              // =================================================

              qualification: {

                relevantQualification:
                  participant
                    .relevantQualification
                    ?.trim()
                  || '',

                qualificationCertificateNumber:
                  participant
                    .qualificationCertificateNumber
                    ?.trim()
                  || '',

                issuingOrganisation:
                  participant
                    .issuingOrganisation
                    ?.trim()
                  || '',

                dateIssuedExpiry:
                  participant
                    .dateIssuedExpiry
                    ?.trim()
                  || '',

                certificateAvailable:
                  participant
                    .certificateAvailable,


                // ===============================================
                // CERTIFICATE FILE
                // ===============================================

                certificateFileName:
                  participant
                    .certificateFileName,

                certificateFileUrl:
                  participant
                    .certificateFileUrl,

                certificateFileType:
                  participant
                    .certificateFileType
              },


              // =================================================
              // IDENTITY
              // =================================================

              identity: {

                idType:
                  participant
                    .idType,

                idNumber:
                  participant
                    .idNumber
                    ?.trim()
                  || '',

                expiryDate:
                  participant
                    .idExpiryDate
                  || null,


                // ===============================================
                // ID / LICENCE FILE
                // ===============================================

                idFileName:
                  participant
                    .idFileName,

                idFileUrl:
                  participant
                    .idFileUrl,

                idFileType:
                  participant
                    .idFileType
              },


              // =================================================
              // DECLARATION
              // =================================================

              declaration: {

                trueAndCorrect:
                  participant
                    .declarationTrueCorrect,

                additionalEvidence:
                  participant
                    .declarationAdditionalEvidence,

                practicalCompetency:
                  participant
                    .declarationPracticalCompetency,

                applicantName:
                  participant
                    .applicantName
                    ?.trim()
                  || '',

                signature:
                  participant
                    .signature
                    ?.trim()
                  || '',

                declarationDate:
                  participant
                    .declarationDate
                  || null
              }
            };
          }
        ),


      submittedDate:
        new Date()
          .toISOString()
    };
  }


  // ============================================================
  // SUBMIT
  // ============================================================

  submitRegistration(): void {

    this.successMessage = '';
    this.errorMessage = '';


    if (!this.validateForm()) {

      return;
    }


    if (this.hasUploadingFiles()) {

      this.errorMessage =
        'Please wait until all documents have finished uploading.';

      return;
    }


    const registrationData =
      this.buildRegistrationData();


    this.isSubmitting =
      true;


    this.adminService
      .saveXEnquiryRegistration(
        this.enquiryId,
        registrationData
      )
      .subscribe({

        next: (response: number) => {

          this.isSubmitting =
            false;


          if (
            response &&
            response > 0
          ) {

            this.successMessage =
              'Registration details submitted successfully.';



            setTimeout(() => {
              this.router.navigate(['/']);
            }, 500);
            return;
          }


          this.errorMessage =
            'Unable to submit registration details.';
        },


        error: (error) => {

          console.error(
            'Registration save error:',
            error
          );


          this.isSubmitting =
            false;


          this.errorMessage =
            typeof error?.error === 'string'
              ? error.error
              : 'Unable to submit registration details.';
        }
      });
  }

  
  
}